import React from 'react';
import type { EpicDetail } from '../../../../../../shared/types/epics';
import { epicSpend, usdSp, cpsColor } from '../format';

// ---------------------------------------------------------------------------
// FORGE-055 — Cost / story-point header card (FR-2)
// ---------------------------------------------------------------------------

export const EfficiencyCard: React.FC<{ epic: EpicDetail }> = ({ epic }) => {
  // Bug fixes don't run the story-point planner.
  if (epic.kind === 'bugfix') return null;
  // Nothing to show until the planner has written total_story_points.
  if (epic.totalStoryPoints === null && epic.costPerStoryPoint === null) return null;

  // FORGE-264: "Epic cost" must equal "Total spent" on the same page — they were
  // diverging because this card preferred `epic.epicTotalCostUsd` (the META
  // field), which — per epicSpend's own note in format.ts — is UNRELIABLE
  // mid-flight and undercounts retries / is never written for some cross-repo
  // epics. Always use the durable live per-story lifetime sum (the same source
  // the "Total spent" total uses), so both show the ACTUAL spend. Cost/SP is
  // derived from that same number so it can't contradict the Epic cost.
  const epicCost = epicSpend(epic).total;
  const isComplete = epic.costPerStoryPoint !== null; // META finalised → "so far" drops off
  const totalSp = epic.totalStoryPoints ?? epic.stories.reduce((n, s) => n + (s.storyPoints ?? 0), 0);
  const costPerSp = totalSp > 0 && epicCost > 0 ? epicCost / totalSp : null;
  return (
    <div
      className="grid grid-cols-3 gap-3"
      data-testid="epic-efficiency-card"
    >
      <Stat
        label="Story points"
        value={totalSp > 0 ? `${totalSp} SP` : '—'}
      />
      <Stat
        label="Epic cost"
        value={epicCost > 0 ? `$${epicCost.toFixed(2)}` : '—'}
        hint={!isComplete && epicCost > 0 ? 'so far' : undefined}
      />
      <Stat
        label="Cost / SP"
        value={costPerSp !== null ? usdSp(costPerSp) : '—'}
        valueColor={costPerSp !== null ? cpsColor(costPerSp) : undefined}
        hint={costPerSp !== null ? (isComplete ? undefined : 'so far') : 'available once story points are set'}
      />
    </div>
  );
};

export const Stat: React.FC<{
  label: string;
  value: string;
  valueColor?: string;
  hint?: string;
}> = ({ label, value, valueColor, hint }) => (
  <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 shadow-card">
    <div className="text-2xs uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
    <div
      className="text-lg font-semibold mt-0.5 tabular-nums"
      style={valueColor ? { color: valueColor } : { color: '#172B4D' }}
    >
      {value}
    </div>
    {hint && <div className="text-2xs text-slate-400 mt-0.5">{hint}</div>}
  </div>
);
